import React, { useState, useEffect, useRef } from "react";
import CryptoJS from "crypto-js";
import { Autocomplete, Button, CircularProgress, Stack, TextField, Box, Grid } from "@mui/material";
import { toast } from "react-toastify";

const CashWithdrawl = () => {
    const [loading, setLoading] = useState(false);
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [amount, setAmount] = useState("");
    const [captureSuccess, setCaptureSuccess] = useState(false);

    const [banks, setBanks] = useState([]);
    const [discoverLoading, setDiscoverLoading] = useState(false);
    const [captureLoading, setCaptureLoading] = useState(false);
    const [deviceDiscovered, setDeviceDiscovered] = useState(false);
    const [selectedBank, setSelectedBank] = useState(null);
    const finalUrlRef = useRef(null);

    // Function to get the user's latitude and longitude
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLatitude(position.coords.latitude);
                    setLongitude(position.coords.longitude);
                },
                (error) => {
                    console.error("Error getting location:", error);
                    alert("Failed to get location. Please check your location settings.");
                }
            );
        } else {
            alert("Geolocation is not supported by this browser.");
        }
    }, []);

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const token = sessionStorage.getItem("jwtToken");
                if (!token) {
                    toast.error("Authentication token missing.");
                    return;
                }

                const response = await fetch("https://api.ipaisa.co.in/myapp/auth/cms/getAllBanks", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(`Error: ${response.status} - ${response.statusText}`);
                }

                const data = await response.json();
                console.log(data.body.data);

                setBanks(data.body.data); // Assuming API returns an array of banks
            } catch (error) {
                console.error("Error fetching banks:", error);
                toast.error("Failed to load bank list.");
            }
        };

        fetchBanks();
    }, []);

    const discoverAvdm = async () => {
        setDiscoverLoading(true);
        let successFlag = false;
        let primaryUrl = window.location.protocol === "https:" ? "https://127.0.0.1:" : "http://127.0.0.1:";

        for (let i = 11100; i <= 11120; i++) {
            try {
                const response = await fetch(primaryUrl + i, {
                    method: "RDSERVICE",
                    mode: "cors",
                    headers: { "Content-Type": "text/xml; charset=utf-8" },
                });

                if (!response.ok) continue;
                const data = await response.text();
                if (data.includes("Mantra")) {
                    finalUrlRef.current = primaryUrl + i;
                    successFlag = true;
                    break;
                }
            } catch (error) {
                console.error("Error discovering AVDM:", error);
            }
        }

        setDiscoverLoading(false);
        if (successFlag) {
            setDeviceDiscovered(true);
            toast.success("Device discovered successfully!");
        } else {
            toast.error("Device discovery failed. Please try again.");
        }
    };

    const handleCashWithdrawal = async (extractedData) => {
        const token = sessionStorage.getItem("jwtToken");
        if (!token) {
            toast.error("Authentication token missing.");
            return;
        }

        const payload = {
            mobileNumber: "9503283004", // Replace with actual mobile number
            transactionAmount: amount, // Use the amount state
            nationalBankIdentificationNumber: "607387", // Replace with actual bank ID
            adhaarNumber: "640088070514", // Replace with actual Aadhaar number
            latitude: latitude,
            longitude: longitude,
            transactionType: "CW",
            ...extractedData,
        };

        try {
            const response = await fetch("https://api.ipaisa.co.in/myapp/auth/cms/cash_withdrawal", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            console.log("Cash withdrawal successful:", data);
            toast.success("Cash withdrawal successful!");
        } catch (error) {
            console.error("Error during cash withdrawal:", error);
            toast.error("Failed to process cash withdrawal.");
        }
    };

    const captureAvdm = async () => {
        if (!finalUrlRef.current) {
            toast.error("Please discover AVDM first.");
            return;
        }

        const captureUrl = `${finalUrlRef.current}/rd/capture`;

        const XML = `<?xml version="1.0"?>
                    <PidOptions ver="1.0">
                        <Opts fCount="1" fType="2" iCount="0" pCount="0" format="0" pidVer="2.0"  timeout="10000"  posh="UNKNOWN" env="P" />
                        <CustOpts>
                            <Param name="mantrakey" value="SOME_KEY_VALUE" />
                        </CustOpts>
                    </PidOptions>`;

        try {
            const response = await fetch(captureUrl, {
                method: "CAPTURE",
                headers: {
                    "Content-Type": "text/xml; charset=utf-8",
                },
                body: XML,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.text();
            console.log("Capture Successful: ", data);

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(data, "text/xml");

            const resp = xmlDoc.getElementsByTagName('Resp')[0];
            const errInfo = resp.getAttribute("errInfo");

            const deviceInfo = xmlDoc.getElementsByTagName('DeviceInfo')[0];
            const additionalInfo = deviceInfo.getElementsByTagName('additional_info')[0];
            const skey = xmlDoc.getElementsByTagName('Skey')[0];
            const pidDataElement = xmlDoc.getElementsByTagName('Data')[0];

            const skeydata = skey.textContent;
            const ci = skey.getAttribute('ci');
            const piddata = pidDataElement.textContent;
            const pidtype = pidDataElement.getAttribute('type');

            const parameters = [];
            const params = additionalInfo.getElementsByTagName('Param');
            for (let i = 0; i < params.length; i++) {
                parameters.push({
                    name: params[i].getAttribute('name'),
                    value: params[i].getAttribute('value')
                });
            }

            const extractedData = {
                resp: {
                    errCode: resp.getAttribute('errCode'),
                    errInfo: errInfo,
                    fCount: resp.getAttribute('fCount'),
                    fType: resp.getAttribute('fType'),
                    nmPoints: resp.getAttribute('nmPoints'),
                    qScore: resp.getAttribute('qScore')
                },
                deviceInfo: {
                    dpId: deviceInfo.getAttribute('dpId'),
                    rdsId: deviceInfo.getAttribute('rdsId'),
                    rdsVer: deviceInfo.getAttribute('rdsVer'),
                    mi: deviceInfo.getAttribute('mi'),
                    mc: deviceInfo.getAttribute('mc'),
                    dc: deviceInfo.getAttribute('dc')
                },
                additionalInfo: parameters,
                ci: ci,
                skeydata: skeydata,
                hmac: xmlDoc.getElementsByTagName('Hmac')[0].textContent,
                piddata: piddata,
                pidtype: pidtype
            };

            if (errInfo === "Success.") {
                setCaptureSuccess(true);
                await handleCashWithdrawal(extractedData);
            } else {
                toast.error(`Capture failed: ${errInfo}`);
            }
        } catch (error) {
            console.error("Error capturing data:", error);
            toast.error("Failed to capture data. Please try again.");
        }
    };

    const isDiscoverCaptureEnabled = selectedBank && amount;

    return (
        <Box sx={{ textAlign: "center", marginTop: "100px", padding: "20px" }}>
            <h2>Uber CMS Web URL Integration</h2>
            <p>Click the button below to proceed with the Fingpay authentication.</p>
            <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        options={banks}
                        getOptionLabel={(option) => option.bankName || ""}
                        onChange={(_, newValue) => setSelectedBank(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Bank"
                                variant="outlined"
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        type="text"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter Amount"
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        type="text"
                        value={latitude}
                        disabled
                        placeholder="Latitude"
                        fullWidth
                        sx={{ mb: 2, backgroundColor: "#f0f0f0" }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        type="text"
                        value={longitude}
                        disabled
                        placeholder="Longitude"
                        fullWidth
                        sx={{ mb: 2, backgroundColor: "#f0f0f0" }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        onClick={handleCashWithdrawal}
                        disabled={!captureSuccess || loading}
                        sx={{
                            padding: "10px 20px",
                            fontSize: "16px",
                            backgroundColor: captureSuccess ? "#28a745" : "#007BFF",
                            color: "white",
                            borderRadius: "5px",
                            "&:hover": {
                                backgroundColor: captureSuccess ? "#218838" : "#0056b3",
                            },
                        }}
                    >
                        {loading ? "Processing..." : captureSuccess ? "Cash Withdrawal" : ""}
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <Stack spacing={2} direction="row" justifyContent="center">
                        <Button
                            variant="contained"
                            onClick={discoverAvdm}
                            disabled={!isDiscoverCaptureEnabled || discoverLoading}
                            sx={{
                                padding: "10px 20px",
                                fontSize: "16px",
                                backgroundColor: "#007BFF",
                                color: "white",
                                borderRadius: "5px",
                                "&:hover": {
                                    backgroundColor: "#0056b3",
                                },
                            }}
                        >
                            {discoverLoading ? <CircularProgress size={24} /> : "Discover"}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={captureAvdm}
                            disabled={!deviceDiscovered || captureLoading || !isDiscoverCaptureEnabled}
                            sx={{
                                padding: "10px 20px",
                                fontSize: "16px",
                                backgroundColor: "#007BFF",
                                color: "white",
                                borderRadius: "5px",
                                "&:hover": {
                                    backgroundColor: "#0056b3",
                                },
                            }}
                        >
                            {captureLoading ? <CircularProgress size={24} /> : "Capture"}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CashWithdrawl;
